<template>
  <div class="custom-color-picker">
    <!-- Color selection canvas (saturation and value) -->
    <div class="color-canvas" ref="canvas" @mousedown="startDraggingCanvas">
      <div class="canvas-indicator" :style="canvasIndicatorStyle"></div>
    </div>

    <!-- Hue slider -->
    <div class="hue-slider" ref="hueSlider" @mousedown="startDraggingHue">
      <div class="slider-indicator" :style="hueIndicatorStyle"></div>
    </div>

    <!-- Numeric inputs for RGB, Alpha, and Hex -->
    <div class="input-grid">
      <div class="input-group">
        <label>R</label>
        <input type="number" v-model.number="computedColor.rgb.r" @input="updateFromRgb" min="0" max="255" />
      </div>
      <div class="input-group">
        <label>G</label>
        <input type="number" v-model.number="computedColor.rgb.g" @input="updateFromRgb" min="0" max="255" />
      </div>
      <div class="input-group">
        <label>B</label>
        <input type="number" v-model.number="computedColor.rgb.b" @input="updateFromRgb" min="0" max="255" />
      </div>
      <div class="input-group">
        <label>A</label>
        <input type="number" v-model.number="alphaInput" @input="updateFromAlpha" min="0" max="1" step="0.01" />
      </div>
      <div class="input-group hex-input">
        <label>Hex</label>
        <input type="text" v-model="hexInput" @input="handleHexInput" placeholder="#RRGGBBAA" />
      </div>
    </div>

    <!-- Preset colors dropdown -->
    <div class="preset-colors">
      <div class="color-swatches">
        <div
          v-for="(color, index) in presetColors"
          :key="index"
          class="swatch"
          :style="{ backgroundColor: color }"
          @click="selectPresetColorFromSwatch(color)"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomColorPicker',
  props: {
    modelValue: {
      type: [String, Object],
      default: '#000000FF',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      canvas: null,
      hueSlider: null,
      hsv: { h: 0, s: 0, v: 1 },
      alpha: 1,
      hexInput: '',
      isDraggingCanvas: false,
      isDraggingHue: false,
      presetColors: [
        '#FF0000', '#FFA500', '#FFFF00', '#008000', '#00FFFF',
        '#0000FF', '#800080', '#FFC0CB', '#000000', '#808080',
        '#FFFFFF', '#D3D3D3', '#ADD8E6', '#90EE90', '#FFB6C1',
      ],
    };
  },
  computed: {
    computedColor() {
      const { r, g, b } = this.hsvToRgb(this.hsv.h, this.hsv.s, this.hsv.v);
      const hex = this.rgbToHex(r, g, b);
      const hex8 = `${hex}${Math.round(this.alpha * 255)
        .toString(16)
        .padStart(2, '0')
        .toUpperCase()}`;
      return { hex, hex8, rgb: { r, g, b }, alpha: this.alpha };
    },
    canvasIndicatorStyle() {
      return {
        left: `${this.hsv.s * 100}%`,
        top: `${(1 - this.hsv.v) * 100}%`,
      };
    },
    hueIndicatorStyle() {
      return {
        left: `${(this.hsv.h / 360) * 100}%`,
      };
    },
    alphaInput: {
      get() {
        return this.alpha;
      },
      set(value) {
        this.alpha = Math.max(0, Math.min(1, value));
        this.emitColor();
      },
    },
  },
  watch: {
    modelValue: {
      handler() {
        this.initializeColor();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    initializeColor() {
      let hex = this.modelValue;
      if (typeof hex === 'object' && hex.hex8) {
        hex = hex.hex8;
      }
      if (!hex || !/^#[0-9A-F]{6,8}$/i.test(hex)) {
        hex = '#000000FF';
      }
      const { h, s, v, a } = this.hexToHsv(hex);
      this.hsv = { h, s, v };
      this.alpha = a;
      this.hexInput = hex.toUpperCase();
    },
    hsvToRgb(h, s, v) {
      h = (h % 360 + 360) % 360;
      s = Math.max(0, Math.min(1, s / 100));
      v = Math.max(0, Math.min(1, v / 100));
      const c = v * s;
      const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
      const m = v - c;
      let r = 0,
        g = 0,
        b = 0;
      if (h >= 0 && h < 60) {
        r = c;
        g = x;
        b = 0;
      } else if (h >= 60 && h < 120) {
        r = x;
        g = c;
        b = 0;
      } else if (h >= 120 && h < 180) {
        r = 0;
        g = c;
        b = x;
      } else if (h >= 180 && h < 240) {
        r = 0;
        g = x;
        b = c;
      } else if (h >= 240 && h < 300) {
        r = x;
        g = 0;
        b = c;
      } else {
        r = c;
        g = 0;
        b = x;
      }
      return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255),
      };
    },
    rgbToHex(r, g, b) {
      return `#${[r, g, b]
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()}`;
    },
    hexToHsv(hex) {
      hex = hex.replace(/^#/, '');
      let r, g, b, a = 1;
      if (hex.length === 8) {
        r = parseInt(hex.slice(0, 2), 16) / 255;
        g = parseInt(hex.slice(2, 4), 16) / 255;
        b = parseInt(hex.slice(4, 6), 16) / 255;
        a = parseInt(hex.slice(6, 8), 16) / 255;
      } else if (hex.length === 6) {
        r = parseInt(hex.slice(0, 2), 16) / 255;
        g = parseInt(hex.slice(2, 4), 16) / 255;
        b = parseInt(hex.slice(4, 6), 16) / 255;
      } else {
        return { h: 0, s: 0, v: 1, a: 1 };
      }
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const d = max - min;
      let h = 0,
        s = max === 0 ? 0 : d / max,
        v = max;
      if (d !== 0) {
        switch (max) {
          case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
            break;
          case g:
            h = ((b - r) / d + 2) * 60;
            break;
          case b:
            h = ((r - g) / d + 4) * 60;
            break;
        }
      }
      return { h, s: s * 100, v: v * 100, a };
    },
    rgbToHsv(r, g, b) {
      r = r / 255;
      g = g / 255;
      b = b / 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const d = max - min;
      let h = 0,
        s = max === 0 ? 0 : d / max,
        v = max;
      if (d !== 0) {
        switch (max) {
          case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
            break;
          case g:
            h = ((b - r) / d + 2) * 60;
            break;
          case b:
            h = ((r - g) / d + 4) * 60;
            break;
        }
      }
      return { h, s: s * 100, v: v * 100 };
    },
    startDraggingCanvas(event) {
      this.isDraggingCanvas = true;
      this.updateCanvas(event);
    },
    updateCanvas(event) {
      if (!this.isDraggingCanvas) return;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));
      this.hsv.s = (x / rect.width) * 100;
      this.hsv.v = (1 - y / rect.height) * 100;
      this.emitColor();
    },
    startDraggingHue(event) {
      this.isDraggingHue = true;
      this.updateHue(event);
    },
    updateHue(event) {
      if (!this.isDraggingHue) return;
      const rect = this.$refs.hueSlider.getBoundingClientRect();
      const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      this.hsv.h = (x / rect.width) * 360;
      this.emitColor();
    },
    handleHexInput() {
      let value = this.hexInput.replace(/^#/, '').toUpperCase();
      if (/^[0-9A-F]{6}$|^[0-9A-F]{8}$/.test(value)) {
        const { h, s, v, a } = this.hexToHsv(`#${value}`);
        this.hsv = { h, s, v };
        this.alpha = a;
        this.emitColor();
      }
    },
    updateFromHsv() {
      this.hsv.h = Math.max(0, Math.min(360, this.hsv.h));
      this.hsv.s = Math.max(0, Math.min(100, this.hsv.s));
      this.hsv.v = Math.max(0, Math.min(100, this.hsv.v));
      this.emitColor();
    },
    updateFromRgb() {
      const { r, g, b } = this.computedColor.rgb;
      const { h, s, v } = this.rgbToHsv(r, g, b);
      this.hsv = { h, s, v };
      this.emitColor();
    },
    updateFromAlpha() {
      this.alpha = Math.max(0, Math.min(1, this.alpha));
      this.emitColor();
    },
    selectPresetColor(event) {
      const hex = event.target.value;
      if (hex) {
        const { h, s, v, a } = this.hexToHsv(hex);
        this.hsv = { h, s, v };
        this.alpha = a;
        this.emitColor();
      }
    },
    selectPresetColorFromSwatch(color) {
      const { h, s, v, a } = this.hexToHsv(color);
      this.hsv = { h, s, v };
      this.alpha = a;
      this.emitColor();
    },
    emitColor() {
      this.hexInput = this.computedColor.hex8;
      this.$emit('update:modelValue', { hex8: this.computedColor.hex8 });
    },
    handleMouseMove(event) {
      if (this.isDraggingCanvas) this.updateCanvas(event);
      if (this.isDraggingHue) this.updateHue(event);
    },
    handleMouseUp() {
      this.isDraggingCanvas = false;
      this.isDraggingHue = false;
    },
  },
  mounted() {
    this.canvas = this.$refs.canvas;
    this.hueSlider = this.$refs.hueSlider;
    this.initializeColor();
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
  },
};
</script>

<style scoped>
.custom-color-picker {
  width: 250px;
  padding: 10px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.color-canvas {
  position: relative;
  width: 230px;
  height: 150px;
  background: linear-gradient(to top, #000, transparent),
              linear-gradient(to right, #fff, hsl(v-bind('hsv.h'), 100%, 50%));
  cursor: crosshair;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.canvas-indicator {
  position: absolute;
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.hue-slider {
  position: relative;
  width: 230px;
  height: 15px;
  background: linear-gradient(
    to right,
    #ff0000 0%,
    #ffff00 17%,
    #00ff00 33%,
    #00ffff 50%,
    #0000ff 67%,
    #ff00ff 83%,
    #ff0000 100%
  );
  cursor: pointer;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.slider-indicator {
  position: absolute;
  width: 6px;
  height: 19px;
  background: #fff;
  border: 1px solid #333;
  border-radius: 2px;
  transform: translateX(-50%);
  top: -2px;
  pointer-events: none;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
}

.input-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 2fr; /* Same as before: more space for Hex */
  gap: 5px;
  margin-bottom: 15px;
}

.input-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.input-group label {
  font-size: 12px;
  color: #333;
  margin-bottom: 5px;
}

.input-group input {
  width: 100%;
  max-width: 45px; /* Increased from 40px to fit values like 255 and 0.99 */
  padding: 5px 2px; /* Reduced horizontal padding to fit within width */
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  box-sizing: border-box;
  font-family: monospace; /* Consistent font for better alignment */
}

.hex-input input {
  max-width: 80px; /* Same as before: wide enough for #RRGGBBAA */
  font-family: monospace;
}

.preset-colors {
  position: relative;
}

.color-swatches {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 5px;
  margin-top: 5px;
}

.swatch {
  width: 30px;
  height: 30px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.1s;
}

.swatch:hover {
  transform: scale(1.1);
}
</style>